import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { API } from 'utils/api';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, ButtonBase, Paper, ClickAwayListener, Typography, Divider, IconButton, Badge, Button } from '@mui/material';

// project imports
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';
import ThemeButton from 'ui-component/ThemeButton';

// assets
import { IconMenu2, IconBell, IconX } from '@tabler/icons-react';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = ({ handleLeftDrawerToggle }) => {
  const theme = useTheme();
  const user = useSelector(state => state.account.user);
  const { pathname } = useLocation();
  console.log('Current user from Redux:', user);
  
  // 菜单状态管理
  const [isAtTop, setIsAtTop] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  
  // 通知状态管理
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 获取通知数据
  const getNotifications = async () => {
    console.log('getNotifications called');
    setLoading(true);
    try {
      console.log('Sending request to /api/notice');
      const res = await API.get('/api/notice');
      console.log('Response from /api/notice:', res.data);
      const { success, data } = res.data;
      if (success) {
        setNotifications(data || []);
        console.log('Notifications updated:', data || []);
      } else {
        console.error('API request failed:', res.data.message);
      }
    } catch (error) {
      console.error('获取通知失败:', error);
      console.error('Error details:', error.response ? error.response.data : error.message);
    } finally {
      setLoading(false);
      console.log('getNotifications completed');
    }
  };
  
  // 组件挂载时获取通知
  useEffect(() => {
    getNotifications();
  }, []);
  
  // 点击通知按钮时刷新通知
  const toggleNotification = () => {
    if (!notificationOpen) {
      getNotifications(); // 打开通知面板时刷新通知
    }
    setNotificationOpen(!notificationOpen);
  };
  
  // 关闭通知弹窗
  const closeNotification = () => {
    setNotificationOpen(false);
  };
  
  // 标记通知为已读
  const markNotificationAsRead = async (notification) => {
    console.log('markNotificationAsRead called with notification:', notification);
    console.log('Current user state:', user);
    console.log('Is user logged in?', !!user);
    console.log('Does user have id?', user && !!user.id);
    if (notification.is_read) {
      console.log('Notification is already read, returning');
      return; 
    }
    if (!user || !user.id) {
      console.log('User is not logged in or user.id is missing:', user);
      return; 
    }
    
    try {
      console.log('Sending API request to mark notification as read:', {
        user_id: user.id,
        notice_id: notification.id
      });
      const response = await API.post('/api/notice/mark_as_read', {
        user_id: user.id,
        notice_id: notification.id
      });
      console.log('API response:', response);
      
      // 更新本地通知状态
      setNotifications(prevNotifications => 
        prevNotifications.map(item => 
          item.id === notification.id ? { ...item, is_read: true } : item
        )
      );
    } catch (error) {
      console.error('标记通知为已读失败:', error);
      console.error('Error details:', error.response ? error.response.data : error.message);
    }
  };

  // 切换通知展开状态并标记为已读
  const toggleNotificationExpand = (id) => {
    console.log('toggleNotificationExpand called with id:', id);
    const notification = notifications.find(item => item.id === id);
    console.log('Found notification:', notification);
    if (notification && !notification.is_read) {
      console.log('Calling markNotificationAsRead for notification:', notification.id);
      markNotificationAsRead(notification);
    }
    
    if (expandedNotification === id) {
      setExpandedNotification(null);
    } else {
      setExpandedNotification(id);
    }
  };

  return (
    <>
      {/* logo & toggler button */}
      <Box
        sx={{
          display: 'flex',
          [theme.breakpoints.down('md')]: {
            width: 'auto'
          }
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
          <LogoSection />
        </Box>
        <Button 
          component={Link} 
          variant="text" 
          to="/" 
          color={pathname === '/' && isAtTop ? 'primary' : 'inherit'}
          sx={{ fontSize: '20px', marginLeft: '50px' ,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'white',
            }}}
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
        >
          首页
        </Button>
        <Button variant="text" 
          component="a"
          href="https://ecn0nfqh6woh.feishu.cn/wiki/space/7558756503222484994"
          target="_blank"
          rel="noopener noreferrer"
          color={pathname === '/apidocx/clothes' ? 'primary' : 'inherit'}
          sx={{ fontSize: '20px', marginLeft: '50px',whiteSpace: 'nowrap',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'white',
            } }}
        >
              教程
        </Button>
        <Button
          component={Link}
          variant="text" 
            to="/"
          color={activeSection === 'wd' ? 'primary' : 'inherit'}
          onClick={() => {
            const section = document.getElementById('features-section1');
            if (section) {
              const yOffset = -80;
              const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({top: y, behavior: 'smooth'});
            }
          }}
          sx={{ fontSize: '20px',marginLeft:'50px',whiteSpace: 'nowrap' ,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'white',
            } }}
        >
          功能
        </Button>
        {/* <Button variant="text" 
          component="a"
          href="https://www.qihuaimage.com/chat"
          target="_blank"
          rel="noopener noreferrer"
          color={pathname === '/apidocx/clothes' ? 'primary' : 'inherit'}
          sx={{ fontSize: '20px', marginLeft: '50px',whiteSpace: 'nowrap',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'white',
            } }}
        >
              直连站
        </Button> */}
        {/* <ButtonBase sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              ...theme.typography.menuButton,
              transition: 'all .2s ease-in-out',
              '&:hover': {
                background: theme.palette.secondary.dark,
                color: theme.palette.secondary.light
              }
            }}
            onClick={handleLeftDrawerToggle}
            color="inherit"
          >
            <IconMenu2 stroke={1.5} size="1.3rem" />
          </Avatar>
        </ButtonBase> */}
      </Box>

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ flexGrow: 1 }} />
      
      {/* 通知按钮和弹窗 */}
      <Box sx={{ mr: 2 }}>
        <ButtonBase sx={{ borderRadius: '12px', position: 'relative' }} onClick={toggleNotification}>
          <Badge
            badgeContent={notifications.filter(n => !n.is_read).length}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                minWidth: 20,
                height: 20,
                fontWeight: 'bold'
              }
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                ...theme.typography.commonAvatar,
                ...theme.typography.mediumAvatar,
                transition: 'all .2s ease-in-out',
                borderColor: theme.typography.menuChip.background,
                backgroundColor: theme.typography.menuChip.background,
                '&[aria-controls="notification-menu"],&:hover': {
                  background: theme.palette.secondary.dark,
                  color: theme.palette.secondary.light
                }
              }}
              color="inherit"
            >
              <IconBell stroke={1.5} size="1.3rem" />
            </Avatar>
          </Badge>
        </ButtonBase>
        
        {/* 通知弹窗 */}
        {notificationOpen && (
          <ClickAwayListener onClickAway={closeNotification}>
            <Paper
              sx={{
                position: 'absolute',
                top: '60px',
                right: '100px',
                width: 300,
                maxHeight: 500,
                minHeight: 400,
                overflowY: 'auto',
                boxShadow: theme.shadows[3],
                borderRadius: 2,
                zIndex: 1000
              }}
            >
              {/* 弹窗头部 */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 2,
                  borderBottom: 1,
                  borderColor: 'divider'
                }}
              >
                <Typography variant="h6">通知中心</Typography>
                <IconButton size="small" onClick={closeNotification}>
                  <IconX stroke={1.5} size="1.2rem" />
                </IconButton>
              </Box>
              
              {/* 通知列表 */}
              <Box sx={{ padding: 1 }}>
                {notifications.length === 0 ? (
                  <Typography variant="body2" sx={{ textAlign: 'center', padding: 3, color: 'text.secondary' }}>
                    暂无通知
                  </Typography>
                ) : (
                  notifications.map((notification) => (
                    <Box
                      key={notification.id}
                      sx={{
                        marginBottom: 1,
                        padding: 2,
                        borderRadius: 1,
                        backgroundColor: notification.is_read ? theme.palette.background.paper : '#f8f9fa',
                        border: 1,
                        borderColor: notification.is_read ? theme.palette.divider : theme.palette.primary.light,
                        cursor: 'pointer',
                        transition: 'all .2s ease-in-out',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                      onClick={() => toggleNotificationExpand(notification.id)}
                    >
                      {/* 通知标题和时间 */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography 
                          variant="body1" 
                          fontWeight={notification.is_read ? "normal" : "bold"}
                          sx={{ color: notification.is_read ? "text.secondary" : "text.primary" }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.time}
                        </Typography>
                      </Box>
                      
                      {/* 通知内容（展开时显示） */}
                      {expandedNotification === notification.id && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          {notification.content}
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </ClickAwayListener>
        )}
      </Box>
      
      {/* <ThemeButton /> */}
      <ProfileSection />
    </>
  );
};

Header.propTypes = {
  handleLeftDrawerToggle: PropTypes.func
};

export default Header;
